import { Suspense, lazy, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { PageSkeleton, RequireClinic, RequireRole, RequireSession } from './app/guards'
import { AppShell } from './app/shell'
import { AccountPage } from './features/account/account-page'
import { AnamnesesPage } from './features/anamneses/anamneses-page'
import { QuestionnairePage } from './features/anamneses/questionnaire-page'
import { QuestionnaireVersionPage } from './features/anamneses/questionnaire-version-page'
import { QuestionnaireVersionsPage } from './features/anamneses/questionnaire-versions-page'
import { AuditLogPage } from './features/audit-log/audit-log-page'
import { ChooseClinicPage } from './features/auth/choose-clinic-page'
import { InvitePage } from './features/auth/invite-page'
import { LoginPage } from './features/auth/login-page'
import { NewPasswordPage } from './features/auth/new-password-page'
import { OAuthConsentPage } from './features/auth/oauth-consent-page'
import { SignInCodePage } from './features/auth/sign-in-code-page'
import { SignupPage } from './features/auth/signup-page'
import { CatalogProcedurePage } from './features/catalog/catalog-procedure-page'
import { CatalogProceduresPage } from './features/catalog/catalog-procedures-page'
import { ClinicProfilePage } from './features/clinic-profile/clinic-profile-page'
import { ClinicPage } from './features/clinics/clinic-page'
import { ClinicsPage } from './features/clinics/clinics-page'
import { DashboardPage } from './features/dashboard/dashboard-page'
import { GenerateDocumentsPage } from './features/documents/generate-documents-page'
import { IntegrationPage } from './features/integrations/integration-page'
import { IntegrationsPage } from './features/integrations/integrations-page'
import { NewIntegrationPage } from './features/integrations/new-integration-page'
import { NewClinicPage } from './features/clinics/new-clinic-page'
import { NetworkPage } from './features/networks/network-page'
import { NetworksPage } from './features/networks/networks-page'
import { NewNetworkPage } from './features/networks/new-network-page'
import { shouldOpenOnboarding, useOnboarding } from './features/onboarding/api'
import { OnboardingPage } from './features/onboarding/onboarding-page'
import { NewPatientPage } from './features/patients/new-patient-page'
import { PatientDuplicatesPage } from './features/patients/patient-duplicates-page'
import { PatientPage } from './features/patients/patient-page'
import { PatientsPage } from './features/patients/patients-page'
import { RegistrationLinkPage } from './features/patients/registration-link-page'
import { SignaturesPage } from './features/signatures/signatures-page'
import { NewTreatmentPlanPage } from './features/treatment-plans/new-treatment-plan-page'
import { RegionsPage } from './features/treatment-plans/regions-page'
import { TreatmentPlanPage } from './features/treatment-plans/treatment-plan-page'
import { TreatmentPlansPage } from './features/treatment-plans/treatment-plans-page'
import { PractitionerPage } from './features/practitioners/practitioner-page'
import { PractitionersPage } from './features/practitioners/practitioners-page'
import { AddProceduresPage } from './features/procedures/add-procedures-page'
import { ImportProceduresPage } from './features/procedures/import-procedures-page'
import { ProcedurePage } from './features/procedures/procedure-page'
import { ProceduresPage } from './features/procedures/procedures-page'
import { NewTemplatePage } from './features/templates/new-template-page'
import { PublishTemplatePage } from './features/templates/publish-template-page'
import { TemplatePage } from './features/templates/template-page'
import { TemplatesPage } from './features/templates/templates-page'
import { OperationsPage } from './features/operations/operations-page'
import { NewTeamMemberPage } from './features/team/new-team-member-page'
import { TeamMemberPage } from './features/team/team-member-page'
import { TeamPage } from './features/team/team-page'
import { NewUserPage } from './features/users/new-user-page'
import { UserPage } from './features/users/user-page'
import { UsersPage } from './features/users/users-page'
import { peekReturnTo } from './lib/return-to'
import { useSession } from './lib/session'
import { routes } from './routes'

const AnamnesisFormPage = lazy(() => import('./features/anamnesis-form/anamnesis-form-page'))
const PhotoSessionPage = lazy(() =>
  import('./features/treatment-plans/photo-session-page').then((m) => ({
    default: m.PhotoSessionPage,
  })),
)

/**
 * Rotas do sistema. Cada área do produto tem o endereço e o item de menu; a tela nasce quando as
 * regras de negócio estão registradas em `docs/areas/` — até lá, `PlaceholderPage` ocuparia o lugar.
 * O styleguide vivo não mora aqui: é o Storybook (`pnpm storybook`, decisão T14).
 */
export function App() {
  return (
    <Routes>
      <Route path={routes.login} element={<LoginPage />} />
      {/* A clínica cria a própria conta (RN-AUT-26). */}
      <Route path={routes.signup} element={<SignupPage />} />
      <Route path={routes.newPassword} element={<NewPasswordPage />} />
      {/* Sem sessão: quem chega pelo link do convite e quem entra com código. */}
      <Route path={routes.invite} element={<InvitePage />} />
      <Route path={routes.signInCode} element={<SignInCodePage />} />
      {/* Consentimento do MCP: a própria tela manda entrar e volta ao pedido (docs/api/mcp.md). */}
      <Route path={routes.oauthConsent} element={<OAuthConsentPage />} />
      {/* A paciente responde pelo link, sem sessão; a tela chega num pacote à parte, para o
          celular dela não baixar o painel da clínica. */}
      <Route
        path={routes.anamnesisForm}
        element={
          <Suspense fallback={null}>
            <AnamnesisFormPage />
          </Suspense>
        }
      />
      {/* O celular que leu o QR de fotos do plano: sem sessão, só o token (docs/api/fotos.md). */}
      <Route
        path={routes.photoSession}
        element={
          <Suspense fallback={null}>
            <PhotoSessionPage />
          </Suspense>
        }
      />

      <Route element={<RequireSession />}>
        {/* Fora da casca: a clínica ainda não foi escolhida. */}
        <Route path={routes.chooseClinic} element={<ChooseClinicPage />} />
        <Route element={<AppShell />}>
          <Route index element={<Home />} />
          {/* A conta da própria pessoa: qualquer perfil, com ou sem clínica ativa (P-AUT-25). */}
          <Route path="/account/:tab?" element={<AccountPage />} />

          <Route
            path={routes.dashboard}
            element={
              <RequireClinic>
                <DashboardPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.patients}
            element={
              <RequireClinic>
                <PatientsPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.newPatient}
            element={
              <RequireClinic>
                <NewPatientPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.patientRegistrationLink}
            element={
              <RequireClinic>
                <RegistrationLinkPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.patientDuplicates(':id')}
            element={
              <Manage>
                <PatientDuplicatesPage />
              </Manage>
            }
          />
          <Route
            path={routes.generateDocuments(':patientId')}
            element={
              <RequireClinic>
                <GenerateDocumentsPage flow="procedure" />
              </RequireClinic>
            }
          />
          <Route
            path={routes.standaloneDocuments(':patientId')}
            element={
              <RequireClinic>
                <GenerateDocumentsPage flow="standalone" />
              </RequireClinic>
            }
          />
          {/* Uma rota só para a ficha, as abas, a anamnese aberta e o documento aberto: a mesma
              instância da página atravessa a troca de aba, e o que foi digitado em Dados não se perde. */}
          <Route
            path="/patients/:id/documents/:appointmentId/:documentId"
            element={
              <RequireClinic>
                <PatientPage />
              </RequireClinic>
            }
          />
          <Route
            path="/patients/:id/:tab?/:anamnesisId?"
            element={
              <RequireClinic>
                <PatientPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.anamneses}
            element={
              <RequireClinic>
                <AnamnesesPage />
              </RequireClinic>
            }
          />
          {/* As perguntas da clínica no questionário: gestora e root com clínica ativa (P-ANA-63). */}
          <Route
            path={routes.anamnesisQuestionnaire}
            element={
              <Manage>
                <QuestionnairePage />
              </Manage>
            }
          />
          <Route
            path={routes.signatures}
            element={
              <RequireClinic>
                <SignaturesPage />
              </RequireClinic>
            }
          />
          {/* Planos de tratamento (docs/areas/planos-de-tratamento.md). `new` antes de `:id`. */}
          <Route
            path={routes.treatmentPlans}
            element={
              <RequireClinic>
                <TreatmentPlansPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.newTreatmentPlan()}
            element={
              <RequireClinic>
                <NewTreatmentPlanPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.treatmentPlanRegions(':id', ':productId')}
            element={
              <RequireClinic>
                <RegionsPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.treatmentPlan(':id')}
            element={
              <RequireClinic>
                <TreatmentPlanPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.admin.users}
            element={
              <Manage>
                <UsersPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.newUser}
            element={
              <Manage>
                <NewUserPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.user(':id')}
            element={
              <Manage>
                <UserPage />
              </Manage>
            }
          />
          {/* Trilha de auditoria da clínica ativa: gestora e root (RN-ADM-60). */}
          <Route
            path={routes.admin.auditLog}
            element={
              <Manage>
                <AuditLogPage />
              </Manage>
            }
          />
          {/* `new` antes de `:id`: a página trata a ausência do id como cadastro novo. */}
          <Route
            path={routes.admin.practitioners}
            element={
              <RequireClinic>
                <PractitionersPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.admin.newPractitioner}
            element={
              <Manage>
                <PractitionerPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.practitioner(':id')}
            element={
              <Manage>
                <PractitionerPage />
              </Manage>
            }
          />

          <Route
            path={routes.onboarding}
            element={
              <Manage>
                <OnboardingPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.clinicProfile}
            element={
              <RequireClinic>
                <ClinicProfilePage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.admin.procedures}
            element={
              <RequireClinic>
                <ProceduresPage />
              </RequireClinic>
            }
          />
          <Route
            path={routes.admin.newProcedure}
            element={
              <Manage>
                <ProcedurePage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.addProcedures}
            element={
              <Manage>
                <AddProceduresPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.importProcedures}
            element={
              <Manage>
                <ImportProceduresPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.procedure(':id')}
            element={
              <Manage>
                <ProcedurePage />
              </Manage>
            }
          />
          {/* Integrações (T53): os apps da clínica ativa, gestora e root. `new` antes de `:id`. */}
          <Route
            path={routes.admin.integrations}
            element={
              <Manage>
                <IntegrationsPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.newIntegration}
            element={
              <Manage>
                <NewIntegrationPage />
              </Manage>
            }
          />
          <Route
            path={routes.admin.integration(':id')}
            element={
              <Manage>
                <IntegrationPage />
              </Manage>
            }
          />

          {/* Painel da Westhetics: só root, com ou sem clínica ativa. */}
          <Route
            path={routes.admin.clinics}
            element={
              <Root>
                <ClinicsPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.newClinic}
            element={
              <Root>
                <NewClinicPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.clinic(':id')}
            element={
              <Root>
                <ClinicPage />
              </Root>
            }
          />
          {/* A equipe da Westhetics: quem é root (RN-AUT-28 a 30). `new` antes de `:id`. */}
          <Route
            path={routes.admin.team}
            element={
              <Root>
                <TeamPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.newTeamMember}
            element={
              <Root>
                <NewTeamMemberPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.teamMember(':id')}
            element={
              <Root>
                <TeamMemberPage />
              </Root>
            }
          />
          {/* O monitoramento da equipe (RN-ADM-94 a 99): só leitura, sobre todas as clínicas. */}
          <Route
            path={routes.admin.operations}
            element={
              <Root>
                <OperationsPage />
              </Root>
            }
          />
          {/* Redes de clínicas: leitura compartilhada de pacientes (RT-06, P-02). `new` antes de `:id`. */}
          <Route
            path={routes.admin.networks}
            element={
              <Root>
                <NetworksPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.newNetwork}
            element={
              <Root>
                <NewNetworkPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.network(':id')}
            element={
              <Root>
                <NetworkPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.catalogProcedures}
            element={
              <Root>
                <CatalogProceduresPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.newCatalogProcedure}
            element={
              <Root>
                <CatalogProcedurePage />
              </Root>
            }
          />
          <Route
            path={routes.admin.catalogProcedure(':id')}
            element={
              <Root>
                <CatalogProcedurePage />
              </Root>
            }
          />
          {/* Questionário padrão da anamnese: versões, rascunho e publicação (RN-ANA-18, P-ANA-66). */}
          <Route
            path={routes.admin.questionnaire}
            element={
              <Root>
                <QuestionnaireVersionsPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.questionnaireVersion(':id')}
            element={
              <Root>
                <QuestionnaireVersionPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.templates}
            element={
              <Root>
                <TemplatesPage />
              </Root>
            }
          />
          <Route
            path={routes.admin.newTemplate}
            element={
              <Root>
                <NewTemplatePage />
              </Root>
            }
          />
          <Route
            path={routes.admin.publishTemplate(':id')}
            element={
              <Root>
                <PublishTemplatePage />
              </Root>
            }
          />
          <Route
            path="/admin/templates/:id"
            element={
              <Root>
                <TemplatePage />
              </Root>
            }
          />
          <Route
            path="/admin/templates/:id/:tab"
            element={
              <Root>
                <TemplatePage />
              </Root>
            }
          />

          <Route path="*" element={<Home />} />
        </Route>
      </Route>
    </Routes>
  )
}

/**
 * A porta de entrada. Quem precisa escolher a clínica escolhe; root sem clínica cai no painel de
 * clínicas; quem administra uma clínica em implantação cai na implantação (RN-ADM-80), salvo se a
 * deixou para depois (RN-ADM-90); os demais, no dashboard.
 */
function Home() {
  const session = useSession()
  const onboarding = useOnboarding({ enabled: session.canManageClinic })
  // Quem entrou para responder a um pedido de conexão do MCP volta a ele (a clínica da conexão é
  // a padrão da pessoa, não a que ela escolheria aqui).
  const returnTo = peekReturnTo()
  if (returnTo) return <Navigate to={returnTo} replace />
  // Vínculo em mais de uma clínica: a escolha vem antes de qualquer destino (RN-AUT-12).
  if (session.mustChooseClinic) return <Navigate to={routes.chooseClinic} replace />
  if (session.isGlobalRoot) return <Navigate to={routes.admin.clinics} replace />
  if (session.canManageClinic && onboarding.isPending) return <PageSkeleton />
  if (session.canManageClinic && shouldOpenOnboarding(onboarding.data)) {
    return <Navigate to={routes.onboarding} replace />
  }
  return <Navigate to={routes.dashboard} replace />
}

/** Telas da Westhetics: catálogo, contratos e clínicas (RT-02). */
function Root({ children }: { children: ReactNode }) {
  return <RequireRole roles={['root']}>{children}</RequireRole>
}

/** Escrita da clínica ativa: gestora, ou root agindo nela (RT-02). */
function Manage({ children }: { children: ReactNode }) {
  return (
    <RequireClinic>
      <RequireRole roles={['manager', 'root']}>{children}</RequireRole>
    </RequireClinic>
  )
}
